// Shared-shows hooks — the support-slot SHARED SHOWS bridge between a
// host band and a FRIEND band. Hand-written over the generated client via the tenant-keyed wrappers, so
// every key is ['band', bandId, …] with bandId from the mounted BandContext — a module cannot fetch outside its
// tenant. Two sides:
//   • HOST — GET/POST /shows/{showId}/shares (the invited/accepted/declined guest list + the "share with a friend
//     band" invite). Invite requires an ACTIVE friendship (422 friendship-required), surfaced verbatim.
//   • GUEST — GET /shared-shows (the acting band's ACCEPTED shared shows, each as the field matrix) + GET
//     /shared-shows/{showId} (one matrix) + POST /shows/{showId}/shares/{shareId}/respond (accept/decline; on
//     accept the response carries the guest-context matrix). The guest acts in its OWN band context (mounted
//     bandId), keyed by the showId + shareId it already holds.
// Every mutation surfaces the server error verbatim and NEVER reports success on an unpersisted write.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

type SharesListRead = components["schemas"]["SharesListRead"];
type ShareDto = components["schemas"]["ShareDto"];
type CreateShareRequest = components["schemas"]["CreateShareRequest"];
type ShareRespondResult = components["schemas"]["ShareRespondResult"];
type SharedShowDetail = components["schemas"]["SharedShowDetail"];
type SharedLineupSlot = components["schemas"]["SharedLineupSlot"];
type SharedRunOfShowItem = components["schemas"]["SharedRunOfShowItem"];

export type {
  SharesListRead,
  ShareDto,
  CreateShareRequest,
  ShareRespondResult,
  SharedShowDetail,
  SharedLineupSlot,
  SharedRunOfShowItem,
};

/** The three share lifecycle states the guest list renders (presentation vocabulary; the server is canonical). */
export type ShareStatus = "invited" | "accepted" | "declined";

// ---- host side ----------------------------------------------------------------------------------------

/**
 * `GET /bands/{bandId}/shows/{showId}/shares` (schedule:read) — the support-slot shares on a show, read if party
 * to either band. Keyed ['band', bandId, 'show', showId, 'shares'] — a PREFIX CHILD of ['band', bandId, 'show'],
 * so the existing `schedule-changed` handler already invalidates it. A 403 surfaces as the query error.
 */
export function useShowShares(showId: string) {
  const { api } = useApp();
  return useBandQuery<SharesListRead>(
    ["show", showId, "shares"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/shows/{showId}/shares", {
        params: { path: { bandId, showId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000 },
  );
}

// The host-side invite is no longer a manual hook (ART-165): a friend band added to a show's lineup is
// auto-invited when the lineup is saved (server-side, ShowsService.ReplaceLineupAsync reconciles the shares
// against the lineup's friend bands). `useShowShares` above stays as the STATUS read the preview chip renders.

// ---- guest side ---------------------------------------------------------------------------------------

/**
 * `GET /bands/{bandId}/shared-shows` (schedule:read) — the acting band's INVITED + ACCEPTED shared shows, each
 * as the guest-context field matrix (basics + flyer + lineup read-only + per-band run-of-show). Keyed
 * ['band', bandId, 'shared-shows']. Callers branch on `status` (ART-165): `PendingInvitesGroup` shows the
 * `invited` ones as Accept/Deny cards on Booking; the `accepted` ones fold into the Booking Upcoming list as
 * ordinary show rows (`buildTimeline`'s `sharedShows`) that open the guest view modal (`GuestShowModal`). A
 * guest responds via `useRespondShare`.
 */
export function useSharedShows() {
  const { api } = useApp();
  return useBandQuery<SharedShowDetail[]>(
    ["shared-shows"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/shared-shows", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

/**
 * `GET /bands/{bandId}/shared-shows/{showId}` (schedule:read) → SharedShowDetail — the guest-context field matrix
 * for ONE shared show. 404 unless an ACCEPTED share + active friendship (the read-only DEFINER; private fields
 * are structurally absent). Keyed ['band', bandId, 'shared-show', showId]; disabled with no target.
 */
export function useSharedShow(showId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<SharedShowDetail>(
    ["shared-show", showId ?? "none"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/shared-shows/{showId}", {
        params: { path: { bandId, showId: showId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: showId != null && showId !== "", staleTime: 15_000 },
  );
}

/**
 * `POST /bands/{bandId}/shows/{showId}/shares/{shareId}/respond` ({accept}) → ShareRespondResult. The guest
 * band accepts or declines a shared date; on accept the result carries the guest-context field matrix (a decline
 * grants no read). Invalidates ['shared-shows'] so an accept surfaces in the guest's shared list. Errors surface
 * verbatim to the caller (403/404/409/422) — no toast (the modal shows the tailored message).
 */
export function useRespondShare(showId: string) {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<ShareRespondResult, { shareId: string; accept: boolean }>(
    async (band, { shareId, accept }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/shares/{shareId}/respond",
        { params: { path: { bandId: band, showId, shareId } }, body: { accept } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "shared-shows"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "shared-show", showId] });
      },
    },
  );
}

/**
 * `PUT /bands/{bandId}/shows/{showId}/shares/{shareId}/link` ({runId, tourId}) → 204 (ART-300). The GUEST band
 * links an accepted shared show into ITS OWN run or tour (both null = a one-off). The host's show and its own link
 * are untouched and invisible here. schedule:write; a pending invite is a 409, another band's run a 400.
 * Invalidates the shared-show reads (they carry the guest's link) and everything run/tour membership feeds —
 * the runs list (name range, picker sub-line), Outstanding Payments, and the linked-days reads.
 */
export function useSetShareLink(showId: string) {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<void, { shareId: string; runId: string | null; tourId: string | null }>(
    async (band, { shareId, runId, tourId }) => {
      const { error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/shows/{showId}/shares/{shareId}/link",
        { params: { path: { bandId: band, showId, shareId } }, body: { runId, tourId } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: () => {
        for (const key of [["shared-shows"], ["shared-show", showId], ["runs"], ["tour-days", "link-options"], ["outstanding"]])
          void qc.invalidateQueries({ queryKey: ["band", bandId, ...key] });
        // Every show's linked-days read: a run-linked day may now attribute to (or away from) this date.
        void qc.invalidateQueries({
          predicate: (q) => q.queryKey[0] === "band" && q.queryKey[1] === bandId && q.queryKey.includes("linked-days"),
        });
      },
    },
  );
}
